import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import ColumnVirtualizationService, {
  ColumnVirtualizationRange,
} from './ColumnVirtualizationService';

const OVERSCAN = 12;
const RECOMPUTE_MARGIN = 4;
// Caps the cold-start fallback so a (re)mount doesn't render every day column at once.
export const MAX_RENDERED_COLUMNS = 60;

export const computeColumnVirtualizationRange = ({
  scrollLeft,
  clientWidth,
  cellWidth,
  totalCount,
}: {
  scrollLeft: number;
  clientWidth: number;
  cellWidth: number;
  totalCount: number;
}): ColumnVirtualizationRange => ({
  startIndex: Math.max(0, Math.floor(scrollLeft / cellWidth) - OVERSCAN),
  endIndex: Math.min(
    totalCount,
    Math.ceil((scrollLeft + clientWidth) / cellWidth) + OVERSCAN,
  ),
});

const subscribe = (callback: () => void) => {
  const subscription = ColumnVirtualizationService.subscribe(callback);
  return () => subscription.unsubscribe();
};

export const useColumnVirtualizationRange = (): ColumnVirtualizationRange =>
  useSyncExternalStore(subscribe, ColumnVirtualizationService.getRange);

export const useColumnVirtualizationOrchestrator = ({
  scrollContainerRef,
  totalCount,
  cellWidth,
}: {
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  totalCount: number;
  cellWidth: number;
}): ColumnVirtualizationRange => {
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !cellWidth || !totalCount) {
      return undefined;
    }

    let rafId: number | null = null;
    let lastStart = -RECOMPUTE_MARGIN - 1;
    let lastEnd = -RECOMPUTE_MARGIN - 1;

    const recompute = () => {
      const { scrollLeft, clientWidth } = el;
      if (clientWidth === 0) return;

      const { startIndex, endIndex } = computeColumnVirtualizationRange({
        scrollLeft,
        clientWidth,
        cellWidth,
        totalCount,
      });

      if (
        Math.abs(startIndex - lastStart) >= RECOMPUTE_MARGIN ||
        Math.abs(endIndex - lastEnd) >= RECOMPUTE_MARGIN
      ) {
        lastStart = startIndex;
        lastEnd = endIndex;
        ColumnVirtualizationService.setRange({ startIndex, endIndex });
      }
    };

    const scheduleRecompute = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        recompute();
      });
    };

    recompute();

    el.addEventListener('scroll', scheduleRecompute, { passive: true });
    window.addEventListener('resize', scheduleRecompute);

    return () => {
      el.removeEventListener('scroll', scheduleRecompute);
      window.removeEventListener('resize', scheduleRecompute);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // Ref is stable; .current read inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, cellWidth]);

  useEffect(
    () => () => {
      ColumnVirtualizationService.reset();
    },
    [],
  );

  return useColumnVirtualizationRange();
};
